import React, { Children, cloneElement, useRef, ReactElement, useEffect } from 'react';
import NavSkeleton from './nav-skeleton';
import clsx from 'clsx';
import { useCommonContextProps, useWindowSize } from '@/hooks';
import { useStore } from '@/store/useStore';
import { getSelectedProvinceCookie, isPharmaprixBanner, setSelectedProvinceCookie } from 'Utils/utils';
import { useSDMCustomerContext } from '@ldp/sdm-contexts';
import { useRouter } from 'next/router';
import { SdmProvince } from '@/data/constants';

// styles
import styles from './nav.module.scss';

interface INavProps {
  children: ReactElement | ReactElement[];
}

export default function Nav({ children }: INavProps): JSX.Element {
  const { locale } = useCommonContextProps();
  const { isDesktop } = useWindowSize({ isModernized: true });

  const focusRef = useRef<HTMLElement | null>(null);

  const setFocusRef = (element: HTMLElement): void => {
    focusRef.current = element;
  };

  const data = useStore(({ headerFooter }) => headerFooter);
  // @ts-ignore
  const { provinceId, setProvinceId } = useSDMCustomerContext();
  const provinceCookie = getSelectedProvinceCookie();

  const updateDataWithNewLinks = (provinceCookie: any) => {
    const navLinksUid = [
      'FrontShopPharmacyServicesLocaledLink',
      'FrontShopHealthAndWellnessLocaledLink',
      'FrontShopElectronicsNavNode',
      'FrontShopPromotionsNavNode',
      'FrontShopPCOptimumNavNode',
      'FrontShopMoreByShoppersNavNode',
    ];

    for (const lang in data) {
      // @ts-ignore
      for (const item of data[lang].navigation) {
        if (navLinksUid.includes(item.uid)) {
          const url = new URL(item.link);
          if (url.searchParams.get('province')) {
            url.searchParams.set('province', provinceCookie);
          } else {
            url.searchParams.append('province', provinceCookie);
          }
          item.link = url.toString();
        }
      }
    }
  };

  const router = useRouter();
  useEffect(() => {
    if (!isPharmaprixBanner()) {
      const provinceParam = router.query.province as string;
      const isProvinceParamValid = provinceParam && Object.values(SdmProvince).includes(provinceParam as SdmProvince);

      // if province parameter is not valid, we default to the value in the cookie.
      // if the cookie is not set, we default to the default provinceid value which is ON
      if (isProvinceParamValid) {
        const selectedProvince = provinceParam !== provinceCookie ? provinceParam : provinceCookie;
        setProvinceId(selectedProvince);
      } else if (provinceCookie) {
        setProvinceId(provinceCookie);
      } else {
        setSelectedProvinceCookie(provinceId);
      }
    }
  }, []);

  useEffect(() => {
    updateDataWithNewLinks(getSelectedProvinceCookie());
  }, [data]);

  useEffect(() => {
    setSelectedProvinceCookie(provinceId);
    updateDataWithNewLinks(getSelectedProvinceCookie());
    if (!isPharmaprixBanner()) {
//      router.replace({ query: { province: provinceId } }, undefined, { shallow: true });
    }
  }, [provinceId]);

  if (!isDesktop) return <div></div>;
  if (!data) return <NavSkeleton />;

  const { navigation } = data[locale];

  return (
    <div className={clsx(styles.desktopNav, styles.navContainer)} role="navigation">
      {Children.map(children, (child) =>
        cloneElement(child, { navigation, key: child.props.name, focusRef, setFocusRef })
      )}
    </div>
  );
}
